import React, { useState, useEffect } from 'react';
import { FaTimes, FaDownload } from 'react-icons/fa';
import { theme, t } from '../theme';
import { overlayCloseProps } from '../utils';
import financasApi from '../services/financasApi';

function fmtMoeda(v) {
  return `R$ ${(Number(v) || 0).toFixed(2).replace('.', ',')}`;
}

export default function ExtratoMensalModal({ onClose }) {
  const hoje = new Date();
  const refMesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;

  const [modo, setModo] = useState('mensal'); // mensal | intervalo
  const [refMes, setRefMes] = useState(refMesAtual);
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [agrupamento, setAgrupamento] = useState('completo');
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState('');

  function paramsAtuais() {
    if (modo === 'mensal') {
      const [ano, mes] = refMes.split('-').map(Number);
      const ini = `${ano}-${String(mes).padStart(2, '0')}-01`;
      const fimDia = new Date(ano, mes, 0).getDate();
      const fmEnd = `${ano}-${String(mes).padStart(2, '0')}-${String(fimDia).padStart(2, '0')}`;
      return { inicio: ini, fim: fmEnd, agrupamento };
    }
    return { inicio, fim, agrupamento };
  }

  async function carregar() {
    setCarregando(true); setErro(''); setDados(null);
    try {
      const res = await financasApi.extrato(paramsAtuais());
      setDados(res.data);
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao gerar extrato');
    } finally { setCarregando(false); }
  }

  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [modo, refMes, agrupamento]);

  async function baixarPdf() {
    setBaixando(true);
    try {
      await financasApi.baixarExtratoPdf(paramsAtuais());
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao baixar PDF');
    } finally { setBaixando(false); }
  }

  return (
    <div style={t.overlay} {...overlayCloseProps(onClose)}>
      <div style={{ ...t.modal, maxWidth: 720 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📋 Extrato Financeiro</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#bbb' }}><FaTimes /></button>
        </div>

        {erro && <div style={t.error}>{erro}</div>}

        {/* Filtros */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${theme.colors.border}` }}>
          <div style={{ display: 'inline-flex', borderRadius: 10, overflow: 'hidden', border: `1px solid ${theme.colors.border}` }}>
            {[{ v: 'mensal', l: 'Mensal' }, { v: 'intervalo', l: 'Intervalo' }].map(o => (
              <button key={o.v} type="button" onClick={() => setModo(o.v)}
                style={{
                  padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: modo === o.v ? theme.colors.primary : '#fff',
                  color: modo === o.v ? '#fff' : theme.colors.textMuted,
                }}>{o.l}</button>
            ))}
          </div>

          {modo === 'mensal' ? (
            <input type="month" value={refMes} onChange={e => setRefMes(e.target.value)}
              style={{ ...t.input, width: 160 }} />
          ) : (
            <>
              <input type="date" value={inicio} onChange={e => setInicio(e.target.value)} style={{ ...t.input, width: 160 }} />
              <input type="date" value={fim} onChange={e => setFim(e.target.value)} style={{ ...t.input, width: 160 }} />
              <button type="button" onClick={carregar} style={t.btnSecondary} disabled={!inicio || !fim}>Atualizar</button>
            </>
          )}

          <div style={{ flex: 1 }}></div>

          <select value={agrupamento} onChange={e => setAgrupamento(e.target.value)} style={{ ...t.select, width: 200 }}>
            <option value="completo">Extrato completo</option>
            <option value="categoria">Por categoria</option>
            <option value="cliente">Por cliente / fornecedor</option>
            <option value="metodo">Por método</option>
          </select>

          <button type="button" onClick={baixarPdf} disabled={baixando || carregando}
            style={{ ...t.btnPrimary, padding: '8px 16px' }}>
            <FaDownload /> {baixando ? 'Gerando...' : 'PDF'}
          </button>
        </div>

        {carregando ? (
          <p style={{ textAlign: 'center', color: '#999', padding: 30 }}>Carregando...</p>
        ) : dados ? (
          <div>
            {/* Cards resumo */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
              {[
                { l: 'Receitas', v: dados.totais.receitas, cor: '#16a34a', bg: '#f0fdf4' },
                { l: 'Despesas', v: dados.totais.despesas, cor: '#dc2626', bg: '#fef2f2' },
                { l: 'Lucro', v: dados.totais.lucro, cor: dados.totais.lucro >= 0 ? '#16a34a' : '#dc2626', bg: '#f9fafb' },
              ].map((c, i) => (
                <div key={i} style={{ background: c.bg, borderRadius: 10, padding: 12, textAlign: 'center', border: `1px solid ${c.cor}22` }}>
                  <div style={{ fontSize: 11, color: theme.colors.textMuted, marginBottom: 4, fontWeight: 600 }}>{c.l}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: c.cor }}>{fmtMoeda(c.v)}</div>
                </div>
              ))}
            </div>

            {/* Grupos (se houver agrupamento) */}
            {agrupamento !== 'completo' && dados.grupos?.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <h4 style={{ fontSize: 13, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Agrupado</h4>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: theme.colors.bgSection || '#F9FAFB' }}>
                      <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: theme.colors.textMuted }}>Grupo</th>
                      <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: '#16a34a' }}>Receitas</th>
                      <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: '#dc2626' }}>Despesas</th>
                      <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600 }}>Lucro</th>
                      <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: theme.colors.textMuted }}>Qtd</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.grupos.map((g, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${theme.colors.borderLight || '#F3F4F6'}` }}>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{g.nome}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#16a34a' }}>{fmtMoeda(g.receitas)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#dc2626' }}>{fmtMoeda(g.despesas)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: g.receitas - g.despesas >= 0 ? '#16a34a' : '#dc2626' }}>{fmtMoeda(g.receitas - g.despesas)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', color: theme.colors.textMuted }}>{g.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Lista de transações */}
            <div>
              <h4 style={{ fontSize: 13, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Transações ({dados.transacoes?.length || 0})</h4>
              {dados.transacoes?.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#bbb', padding: 20 }}>Sem transações no período.</p>
              ) : (
                <div style={{ maxHeight: 280, overflow: 'auto' }}>
                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                      <tr style={{ background: theme.colors.bgSection || '#F9FAFB' }}>
                        <th style={{ textAlign: 'left', padding: '6px 10px', color: theme.colors.textMuted }}>Data</th>
                        <th style={{ textAlign: 'left', padding: '6px 10px', color: theme.colors.textMuted }}>Descrição</th>
                        <th style={{ textAlign: 'left', padding: '6px 10px', color: theme.colors.textMuted }}>Categoria</th>
                        <th style={{ textAlign: 'left', padding: '6px 10px', color: theme.colors.textMuted }}>Método</th>
                        <th style={{ textAlign: 'right', padding: '6px 10px', color: theme.colors.textMuted }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.transacoes.map(tx => (
                        <tr key={tx.id} style={{ borderBottom: `1px solid ${theme.colors.borderLight || '#F3F4F6'}` }}>
                          <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{tx.data ? new Date(tx.data + 'T12:00:00').toLocaleDateString('pt-BR') : ''}</td>
                          <td style={{ padding: '6px 10px' }}>{tx.descricao || (tx.tipo === 'receita' ? (tx.cliente_nome || '—') : (tx.fornecedor || '—'))}</td>
                          <td style={{ padding: '6px 10px', color: theme.colors.textMuted }}>{tx.categoria_nome}</td>
                          <td style={{ padding: '6px 10px', color: theme.colors.textMuted }}>{tx.metodo_pagamento_nome}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, color: tx.tipo === 'receita' ? '#16a34a' : '#dc2626' }}>
                            {tx.tipo === 'despesa' ? '−' : ''}{fmtMoeda(tx.valor)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
